// Dedicated Web Worker lane broker with full AnalysisStamp validation and stale-job termination.
// One AnalysisBroker = one independent Worker lane (e.g. background eligibility lane vs. action AI/crisis lane).
export interface AnalysisStamp {
  matchId: string;
  sessionId: number;
  epoch: number;
  version: number;
  boardHash: string;
  sideToMove: 'cho' | 'han';
  requestSide: 'cho' | 'han';
  turnGeneration: number;
  engineVersion: string;
}

export type AnalysisJobKind = 'eligibility' | 'coaching' | 'crisis' | 'ai';

export interface WorkerLike {
  onmessage: ((event: { data: any }) => void) | null;
  onerror: ((event: any) => void) | null;
  postMessage(data: any): void;
  terminate(): void;
}

export type AnalysisOutcome<T = any> =
  | { status: 'ok'; value: T }
  | { status: 'discarded'; reason: string }
  | { status: 'error'; reason: string };

export function stampKey(s: AnalysisStamp): string {
  return JSON.stringify([
    s.matchId,
    s.sessionId,
    s.epoch,
    s.version,
    s.boardHash,
    s.sideToMove,
    s.requestSide,
    s.turnGeneration,
    s.engineVersion
  ]);
}

export class AnalysisBroker {
  private worker: WorkerLike | null = null;
  private sequence = 0;
  private pending: {
    id: number;
    key: string;
    requestKey: string;
    promise: Promise<AnalysisOutcome>;
    resolve: (value: AnalysisOutcome) => void;
  } | null = null;

  constructor(
    private createWorker: () => WorkerLike,
    private currentStamp: (requestSide?: 'cho' | 'han') => AnalysisStamp
  ) {}

  public isBusy(): boolean {
    return this.pending !== null;
  }

  public warmup(): void {
    if (this.worker) return;
    try {
      this.worker = this.createWorker();
    } catch {
      this.worker = null;
    }
  }

  public request<T = any>(
    kind: AnalysisJobKind,
    payload: any,
    stamp: AnalysisStamp
  ): Promise<AnalysisOutcome<T>> {
    const key = stampKey(stamp);
    if (key !== stampKey(this.currentStamp(stamp.requestSide))) {
      return Promise.resolve({ status: 'discarded', reason: 'stale_before_start' });
    }
    const requestKey = key + ':' + kind + ':' + JSON.stringify(payload);
    // Repeated HUD updates must not restart the exact same in-flight search.
    if (this.pending?.requestKey === requestKey) {
      return this.pending.promise as Promise<AnalysisOutcome<T>>;
    }
    // A running synchronous search in the Worker cannot read a cancel message until it yields; terminate on supersession.
    if (this.pending) {
      this.cancel('superseded');
    }

    const id = ++this.sequence;
    let resolve!: (value: AnalysisOutcome) => void;
    const promise = new Promise<AnalysisOutcome>(r => {
      resolve = r;
    });
    this.pending = { id, key, requestKey, promise, resolve };

    try {
      if (!this.worker) {
        this.worker = this.createWorker();
      }
      const worker = this.worker;
      worker.onmessage = event => {
        const data = event.data;
        if (this.worker !== worker || !this.pending || data?.id !== this.pending.id) return;
        const pending = this.pending;
        this.pending = null;
        if (pending.key !== stampKey(this.currentStamp(stamp.requestSide)) || data.stampKey !== pending.key) {
          pending.resolve({ status: 'discarded', reason: 'stale_result' });
        } else if (data.ok) {
          pending.resolve({ status: 'ok', value: data.value });
        } else {
          pending.resolve({ status: 'error', reason: data.error || 'analysis_failed' });
        }
      };
      worker.onerror = () => {
        if (this.worker !== worker) return;
        const pending = this.pending;
        this.pending = null;
        worker.terminate();
        this.worker = null;
        pending?.resolve({ status: 'error', reason: 'worker_error' });
      };
      worker.postMessage({ id, kind, payload, stampKey: key });
    } catch {
      const pending = this.pending;
      this.pending = null;
      this.worker?.terminate();
      this.worker = null;
      pending?.resolve({ status: 'error', reason: 'worker_unavailable' });
    }

    return promise as Promise<AnalysisOutcome<T>>;
  }

  public cancel(reason = 'state_changed', eagerRespawn = false): void {
    const pending = this.pending;
    this.pending = null;
    if (pending) {
      this.worker?.terminate();
      this.worker = null;
      pending.resolve({ status: 'discarded', reason });
      if (eagerRespawn) {
        this.warmup();
      }
    }
  }

  public dispose(): void {
    this.cancel('disposed');
    this.worker?.terminate();
    this.worker = null;
  }
}
